import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { GUID, InternalUserProfile, Tenant } from '@ndvr/auth-api';

import { useTenantAdvisorsForTenant } from 'views/admin/tenantTeam/tenant-team-queries';
import { FormSelect } from 'widgets';

const FIELD_NAME = 'primaryAdvisorGUID';

export const PrimaryAdvisorSelector = ({
    hideLabel,
    tenantGUID,
    defaultAdvisorGUID,
}: {
    hideLabel?: boolean;
    tenantGUID: GUID<Tenant> | undefined;
    defaultAdvisorGUID?: GUID<InternalUserProfile>;
}) => {
    const { getValues, resetField, setValue } = useFormContext();
    const { advisors, isPending: advisorsLoading } = useTenantAdvisorsForTenant(tenantGUID);

    useEffect(() => {
        if (advisorsLoading) {
            return;
        }

        /**
         * Timeout is required here to allow the options be rendered so
         * react-hook-form can find the corresponding option to the passed defaultValue.
         */
        const timeout = setTimeout(() => {
            /**
             * React-hook-form needs the options to be available to determine the value of the field.
             * If options arrive later than the defaultValues is presented, the field needs to be reset
             * to populate it with value.
             */
            const selectedAdvisorGUID = getValues(FIELD_NAME);
            const hasSelectedAdvisor = advisors.some(
                ({ internalUserGUID }) => internalUserGUID === selectedAdvisorGUID,
            );
            const hasDefaultAdvisor = advisors.some(({ internalUserGUID }) => internalUserGUID === defaultAdvisorGUID);
            const nextAdvisorGUID = hasSelectedAdvisor
                ? selectedAdvisorGUID
                : hasDefaultAdvisor
                  ? defaultAdvisorGUID
                  : undefined;

            if (nextAdvisorGUID) {
                resetField(FIELD_NAME, { defaultValue: nextAdvisorGUID });
            } else {
                setValue(FIELD_NAME, undefined);
            }
        }, 0);

        return () => clearTimeout(timeout);
    }, [advisors, advisorsLoading, defaultAdvisorGUID, getValues, resetField, setValue, tenantGUID]);

    return (
        <FormSelect
            name={FIELD_NAME}
            hideLabel={hideLabel}
            label="Primary Advisor"
            isLoading={advisorsLoading}
            disabled={!tenantGUID}
        >
            <option></option>
            {advisors?.map((advisor) => (
                <option key={advisor.internalUserGUID} value={advisor.internalUserGUID}>
                    {advisor.name || advisor.email || 'Claro Team Member'}
                </option>
            ))}
        </FormSelect>
    );
};
